import { Howl, Howler } from 'howler';
import { DEFAULT_MUSIC_TRACK_ID, getMusicTrack, type MusicTrackId } from '@/config/music.config';

const TARGET_VOLUME = 0.28;
const FADE_MS = 1200;

interface ActiveTrack {
  trackId: MusicTrackId;
  sourceIndex: number;
  howl: Howl;
}

function extensionOf(src: string): string | null {
  const match = /\.([a-z0-9]+)(?:\?|#|$)/i.exec(src);
  return match?.[1]?.toLowerCase() ?? null;
}

class MusicController {
  private enabled = false;
  private trackId: MusicTrackId = DEFAULT_MUSIC_TRACK_ID;
  private active: ActiveTrack | null = null;
  private unlockArmed = false;
  private warnedMissing = new Set<MusicTrackId>();

  setMusicState(enabled: boolean, trackId: MusicTrackId): void {
    const trackChanged = this.trackId !== trackId;
    this.enabled = enabled;
    this.trackId = trackId;

    if (!enabled) {
      this.fadeOut(this.active);
      this.active = null;
      return;
    }

    if (!trackChanged && this.active?.trackId === trackId) {
      this.fadeIn(this.active);
      return;
    }

    const previous = this.active;
    const next = this.startTrack(trackId, 0);
    if (next) {
      this.active = next;
      this.fadeIn(next);
      this.fadeOut(previous);
    }
  }

  stop(): void {
    this.enabled = false;
    this.active?.howl.stop();
    this.active?.howl.volume(0);
    this.active = null;
  }

  private startTrack(trackId: MusicTrackId, sourceIndex: number): ActiveTrack | null {
    const track = getMusicTrack(trackId);
    const supportedSources = track.sources.filter((src) => {
      const ext = extensionOf(src);
      return ext === null || Howler.codecs(ext);
    });
    const src = supportedSources[sourceIndex];

    if (!src) {
      this.warnMissing(trackId, 'Brak wspieranego formatu audio w tej przegladarce.');
      return null;
    }

    const active: ActiveTrack = {
      trackId,
      sourceIndex,
      howl: new Howl({
        src,
        html5: true,
        loop: true,
        volume: 0,
        preload: false,
        onloaderror: (_soundId, error) => this.handleLoadError(active, supportedSources.length, error),
        onplayerror: (_soundId, error) => this.handlePlayError(error),
      }),
    };

    active.howl.play();
    return active;
  }

  private fadeIn(active: ActiveTrack): void {
    if (!active.howl.playing()) active.howl.play();
    active.howl.fade(active.howl.volume(), TARGET_VOLUME, FADE_MS);
  }

  private fadeOut(active: ActiveTrack | null): void {
    if (!active) return;

    if (!active.howl.playing()) {
      active.howl.stop();
      active.howl.unload();
      return;
    }

    active.howl.fade(active.howl.volume(), 0, FADE_MS);
    window.setTimeout(() => {
      active.howl.stop();
      active.howl.unload();
    }, FADE_MS + 80);
  }

  private handleLoadError(active: ActiveTrack, sourceCount: number, error: unknown): void {
    active.howl.unload();

    if (!this.enabled || this.trackId !== active.trackId || this.active !== active) return;

    const nextSourceIndex = active.sourceIndex + 1;
    if (nextSourceIndex < sourceCount) {
      const replacement = this.startTrack(active.trackId, nextSourceIndex);
      if (replacement) {
        this.active = replacement;
        this.fadeIn(replacement);
      }
      return;
    }

    this.active = null;
    this.warnMissing(active.trackId, `Nie udalo sie zaladowac muzyki: ${String(error)}`);
  }

  private handlePlayError(error: unknown): void {
    if (this.unlockArmed || !this.active) return;
    this.unlockArmed = true;

    this.active.howl.once('unlock', () => {
      this.unlockArmed = false;
      if (this.enabled) {
        const replacement = this.startTrack(this.trackId, 0);
        if (replacement) {
          this.active = replacement;
          this.fadeIn(replacement);
        }
      }
    });

    console.debug('[MusicController] Czekam na gest uzytkownika przed startem muzyki.', error);
  }

  private warnMissing(trackId: MusicTrackId, message: string): void {
    if (this.warnedMissing.has(trackId)) return;
    this.warnedMissing.add(trackId);
    const track = getMusicTrack(trackId);
    console.warn(`[MusicController] ${message} Sprawdz pliki audio dla utworu "${track.name}".`);
  }
}

export const musicController = new MusicController();
